import { inject } from '@angular/core';
import {
  callApiMethod,
  setError,
  setLoaded,
  setLoading,
  withCallState,
  withModel,
  withModelCrud,
} from '@kinet-open-network/client/shared/data-access/state';
import { apiFilter } from '@kinet-open-network/shared/data-access/api';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { MoveDeviceData } from '../models/api/move-device.model';
import { SetInvoicingPeriod } from '../models/api/set-invoicing-period.model';
import { CustomerDevice } from '../models/customer-device.model';
import { CustomerDevicesService } from '../services/customer-devices.service';

export const CustomerDevicesStore = signalStore(
  { protectedState: false },
  withCallState(),
  withModel<CustomerDevice>(),
  withModelCrud(CustomerDevicesService),
  withState<{ data: CustomerDevice[] }>({ data: [] }),
  withMethods((store, service = inject(CustomerDevicesService)) => ({
    loadByApId: (customerId?: number | null, apId?: string | null) => {
      if (!customerId || !apId) {
        patchState(store, { data: [] });
        return;
      }

      patchState(store, setLoading());

      return callApiMethod(
        () =>
          service.query({
            filters: apiFilter<CustomerDevice>()
              .customerId(customerId)
              .apId(apId),
            perPage: -1,
            sort: ['createdAt'],
          }),
        {
          next: (resp) => patchState(store, { data: resp.data }, setLoaded()),
          error: (err) => patchState(store, setError(err)),
        },
      );
    },
    returnDevice: (id: string, returnDate?: Date | null) => {
      return callApiMethod(() => service.returnDevice(id, returnDate), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    markFinePaid: (id: string, finePaidAt?: Date | null) => {
      return callApiMethod(() => service.markFinePaid(id, finePaidAt), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    setDeliveryDate: (id: string, deliveryDate: Date | null) => {
      return callApiMethod(() => service.setDeliveryDate(id, deliveryDate), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    setState: (id: string, data: Partial<CustomerDevice>) => {
      return callApiMethod(() => service.updateState(id, data), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    changeAp: (id: string, data: MoveDeviceData) => {
      return callApiMethod(() => service.changeAp(id, data), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    setInvoicingPeriod: (id: string, data: SetInvoicingPeriod) => {
      return callApiMethod(() => service.setInvoicingPeriod(id, data), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    setInvoiceState: (id: string, invoiceDate: Date | null) => {
      return callApiMethod(() => service.setInvoiceState(id, invoiceDate), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
  })),
);
